import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment/moment';

import {
  getUtilityConfigurationSourceDisplayName,
  isUtilityConfigurationPersonSource,
  isUtilityConfigurationPmsSource,
  UTILITY_CONFIGURATION_SOURCE_ADMIN,
  UTILITY_CONFIGURATION_SOURCE_DEFAULT,
  UTILITY_CONFIGURATION_SOURCE_PARTNER,
  utilityConfigurationProvenancePropType,
} from 'client/constants/utilityConfigurationSource';
import SrTooltip from 'client/components/SrTooltip';

import './SrUtilityConfigurationSourceMarker.less';

const PersonIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="5" />
    <path d="M20 21a8 8 0 0 0-16 0" />
  </svg>
);

const IntegrationIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const DefaultsIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 21v-7" />
    <path d="M4 10V3" />
    <path d="M12 21v-9" />
    <path d="M12 8V3" />
    <path d="M20 21v-5" />
    <path d="M20 12V3" />
    <path d="M1 14h6" />
    <path d="M9 8h6" />
    <path d="M17 16h6" />
  </svg>
);

const DEFAULT_SOURCE_CONFIG = {
  icon: <DefaultsIcon />,
  badgeLabel: 'Defaults',
  calloutLabel: getUtilityConfigurationSourceDisplayName(
    UTILITY_CONFIGURATION_SOURCE_DEFAULT,
  ),
  accessibleLabel: 'Utility settings using partner defaults',
  helpText: 'No one has reviewed these utility settings yet.',
};

const getSourceConfig = (source) => {
  const displayName = getUtilityConfigurationSourceDisplayName(source);

  if (isUtilityConfigurationPersonSource(source)) {
    const isAdminSource = source === UTILITY_CONFIGURATION_SOURCE_ADMIN;
    const reviewerLabel = isAdminSource ? displayName : 'your team';
    return {
      icon: <PersonIcon />,
      badgeLabel: isAdminSource ? 'UP' : displayName,
      calloutLabel: `Set by ${reviewerLabel}`,
      accessibleLabel: `Utility settings set by ${reviewerLabel}`,
      helpText: isAdminSource
        ? `${displayName} reviewed and saved these utility settings.`
        : 'Your team reviewed and saved these utility settings.',
    };
  }

  if (isUtilityConfigurationPmsSource(source)) {
    return {
      icon: <IntegrationIcon />,
      badgeLabel: 'PMS Sync',
      calloutLabel: `Synced from ${displayName}`,
      accessibleLabel: `Utility settings synced from ${displayName}`,
      helpText: `These utility settings were synced from ${displayName}.`,
    };
  }

  return DEFAULT_SOURCE_CONFIG;
};

const SrUtilityConfigurationSourceMarker = ({ children, provenance, tooltipId, variant }) => {
  const provenanceSource = provenance?.source;
  const isPersonSource = isUtilityConfigurationPersonSource(provenanceSource);
  const isPmsSource = isUtilityConfigurationPmsSource(provenanceSource);
  const sourceConfig = getSourceConfig(provenanceSource);
  const hasExplicitSource = isPersonSource || isPmsSource;
  const isBadge = variant === 'badge';
  const sourceLabel = isBadge ? sourceConfig.badgeLabel : sourceConfig.calloutLabel;
  const updatedAt = provenance?.updatedAt
    ? moment(provenance.updatedAt).format('MMM D, YYYY')
    : null;

  const sourceStyleName = classnames(
    'source',
    variant === 'callout' ? 'sourceCallout' : 'sourceBadge',
    {
      sourceDefault: !hasExplicitSource,
      sourcePartner: provenanceSource === UTILITY_CONFIGURATION_SOURCE_PARTNER,
      sourceAdmin: provenanceSource === UTILITY_CONFIGURATION_SOURCE_ADMIN,
      sourcePms: isPmsSource,
    },
  );
  const sourceContents = (
    <>
      {sourceConfig.icon}
      <span aria-hidden={isBadge ? true : undefined}>{sourceLabel}</span>
    </>
  );
  const source = isBadge ? (
    <button
      aria-describedby={tooltipId}
      aria-label={sourceConfig.accessibleLabel}
      styleName={sourceStyleName}
      type="button"
      onClick={(event) => event.currentTarget.blur()}
    >
      {sourceContents}
    </button>
  ) : (
    <span styleName={sourceStyleName}>{sourceContents}</span>
  );

  const sourceDetails = isBadge ? (
    source
  ) : (
    <span styleName="sourceDetails">
      {source}
      {updatedAt && hasExplicitSource && <span styleName="updatedAt">· {updatedAt}</span>}
    </span>
  );
  const helpIcon = isBadge ? (
    <span aria-hidden="true" />
  ) : (
    <button
      aria-describedby={tooltipId}
      aria-label="Learn more about this source"
      styleName="helpButton"
      type="button"
    >
      <span aria-hidden="true" styleName="helpIcon" />
    </button>
  );
  const sourceWithHelp = (
    <SrTooltip
      className="utility-configuration-source-tooltip"
      icon={helpIcon}
      label={sourceDetails}
    >
      <span id={tooltipId} role="tooltip">
        {sourceConfig.helpText}
      </span>
    </SrTooltip>
  );

  if (variant === 'badge') {
    return (
      <span
        data-testid="utility-configuration-source-marker"
        styleName="marker"
      >
        {sourceWithHelp}
      </span>
    );
  }

  return (
    <div
      data-testid="utility-configuration-source-callout"
      styleName="callout"
    >
      <div
        styleName="sourceGrid"
        role="note"
        aria-label={`Configuration source: ${sourceConfig.calloutLabel}`}
      >
        <span styleName="sourceLabel">Current source</span>
        <div styleName="sourceValue">
          {sourceWithHelp}
        </div>
      </div>
      {children && <div styleName="children">{children}</div>}
    </div>
  );
};

SrUtilityConfigurationSourceMarker.propTypes = {
  children: PropTypes.node,
  provenance: utilityConfigurationProvenancePropType,
  tooltipId: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['badge', 'callout']),
};

SrUtilityConfigurationSourceMarker.defaultProps = {
  children: null,
  provenance: null,
  variant: 'badge',
};

SrUtilityConfigurationSourceMarker.displayName = 'SrUtilityConfigurationSourceMarker';

export default SrUtilityConfigurationSourceMarker;
